'use client';
import React, { useEffect, useState } from 'react';
import { AuthOnboardingShell } from '../_components';
import { MimaButton, MimaText, MimaToastUtil } from '@/components';
import styles from '@/assets/styles/pages/partnership/PartnerOnboarding.module.scss';
import { useRouter, useSearchParams } from 'next/navigation';
import constant from '@/utils/constant';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IDefaultObject } from '@/interfaces';
import { passwordRegex, phoneRegExp, removeEmptyKeys } from '@/utils/utils';
import { UserController } from '@/controllers';
import { userStore } from '@/stores';
import { SignupPhase1, SignupPhase2 } from './_signupPhases';
import { IoArrowBackCircle } from 'react-icons/io5';

const SignupLayout = () => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [signupPhase, setSignupPhase] = useState<number>(1);

  const searchParams = useSearchParams();

  const referrerCode = searchParams.get('referrerCode') || '';

  const goToLogin = () => {
    router.push(constant.Routes.Login);
  };

  const goToForgotPassword = () => {
    router.push(constant.Routes.ForgetPassword);
  };

  const goBack = () => {
    setSignupPhase(1);
  };

  const validationSchema = z
    .object({
      fullname: z
        .string()
        .min(1, 'Full Name is required')
        .refine(
          (value) => {
            const names = value.split(' ');
            return names.length > 1 && names[0].length > 1 && names[1].length > 1;
          },
          {
            message: 'First and Last name is required',
          },
        ),
      tradingName: z.string().min(1, 'Business Name is required'),
      mobile: z.string().regex(phoneRegExp, 'Phone number is not valid').min(1, 'Phone number is required'),
      email: z.string().email('Invalid email address').min(1, 'Email is required'),
      password: z.string().regex(passwordRegex, 'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character'),
      businessRegNum: z.string().optional(),
      website: z.string().optional(),
      twitterHandle: z.string().optional(),
      instagramHandle: z.string().optional(),
      referralSource: z.string().min(1, 'Select how you heard of fash creatives'),
      referredBy: z.string().optional(),
    })
    .refine(
      (data) => {
        const { businessRegNum, website, twitterHandle, instagramHandle } = data;
        const filledInputs = [businessRegNum, website, twitterHandle, instagramHandle].filter(Boolean);
        return filledInputs.length >= 1;
      },
      {
        message: 'At least one of the following must be filled: business registration number, website address, Twitter handle, or Instagram handle',
        path: ['website'],
      },
    );

  type ISignup = z.infer<typeof validationSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors, touchedFields },
    trigger,
    setValue,
  } = useForm<IDefaultObject>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (referrerCode) {
      setValue('referredBy', referrerCode);
    }
  }, [referrerCode]);

  const onSubmit = async (values: IDefaultObject) => {
    setLoading(true);
    const names = values.fullname.trim().split(' ');

    const { fullname, ...rest } = values;

    const payload = {
      ...rest,
      firstname: names[0],
      lastname: names.length > 2 ? names[2] : names[1],
      middlename: names.length > 2 ? names[1] : '',
      isFash: true,
    };
    removeEmptyKeys(payload);

    const { status, errorMessage } = await UserController.signup(payload);
    if (status === constant.Success) {
      setLoading(false);
      userStore.setOtpType(constant.OtpType.ONBOARDING);
      return router.push(constant.Routes.Otp);
    }
    setLoading(false);
    return MimaToastUtil.error({
      message: errorMessage,
    });
  };

  return (
    <AuthOnboardingShell>
      <div className={styles.logInForm}>
        <div className={styles.logintext__center}>
          <MimaText color="var(--color-dark)" variant="subtitleBold">
            Sign Up
          </MimaText>
          {/* <p className={styles.logintext__p}>
            Already have an account?{' '}
            <span className={styles.clickable} onClick={goToLogin}>
              Log In
            </span>
          </p> */}
        </div>
        {signupPhase === 2 ? (
          <div className={styles.goBack}>
            <IoArrowBackCircle onClick={goBack} />
            <MimaText>Go Back</MimaText>
          </div>
        ) : (
          ''
        )}
        <form className={styles.loginform} onSubmit={handleSubmit(onSubmit)}>
          {signupPhase === 1 && <SignupPhase1 setSignupPhase={setSignupPhase} control={control} touchedFields={touchedFields} trigger={trigger} />}

          {signupPhase === 2 && (
            <>
              <SignupPhase2 setSignupPhase={setSignupPhase} control={control} touchedFields={touchedFields} trigger={trigger} />
              <MimaButton title="Continue" type="submit" loading={loading} />
            </>
          )}
        </form>

        {signupPhase === 2 ? (
          <div className={`${styles.logintext__p} ${styles.mt_40}`}>
            Creating an account means you agree with <br /> <span className={styles.clickable}> Mima’s Terms of Service and Privacy Policy</span>
          </div>
        ) : (
          ''
        )}
      </div>
    </AuthOnboardingShell>
  );
};

export default SignupLayout;
